import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { SOKYU_TERMS_URL } from '../constants/terms';

interface TermsConsentPopupProps {
  open: boolean;
  /** 同意チェック後に「同意して続ける」を押したときのハンドラ（同意記録APIの呼び出し等） */
  onAgree: () => void;
  /** 同意せずに閉じる（口座登録に進めないため、呼び出し側で前画面へ戻す等） */
  onClose: () => void;
  /** 同意記録の通信中フラグ */
  loading?: boolean;
  /** 同意記録失敗時のエラー文言 */
  errorMessage?: string;
}

/**
 * 口座登録（即給申込）時の即給利用規約 同意ポップアップ（課題88）。
 *
 * 口座登録は即給 API への利用者登録（agreeFlg="1" 送信）を伴うため、その直前に
 * ワーカー本人の同意を明示的に取得・記録する。Re:crew の利用規約・プライバシーポリシーは
 * 新規ユーザー登録時に別途同意済みのため、この画面では表示せず、即給（前払給与）の
 * 利用規約への同意のみを取得する。チェックされるまで「同意して続ける」を無効化する。
 */
const TermsConsentPopup: React.FC<TermsConsentPopupProps> = ({
  open,
  onAgree,
  onClose,
  loading = false,
  errorMessage,
}) => {
  const [agreed, setAgreed] = useState(false);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>即給利用規約への同意</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          口座登録（即給のお申し込み）にあたり、即給の利用規約への同意が必要です。
        </Typography>
        {errorMessage && (
          <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
            {errorMessage}
          </Typography>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={loading}
            />
          }
          label={
            <Box component="span" sx={{ fontSize: '0.85rem' }}>
              <Link href={SOKYU_TERMS_URL} target="_blank" rel="noopener" sx={{ mr: 0.5 }}>
                即給利用規約
              </Link>
              に同意します。
            </Box>
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading} sx={{ flex: 1 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={onAgree}
          disabled={!agreed || loading}
          sx={{ flex: 2 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : '同意して続ける'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsConsentPopup;
